import React, { useLayoutEffect, useRef } from 'react';

// The generic "something is happening" indicator — the same charcoal hairline
// wave motif as the plugin sign-in screen (HandoffWaveform), but a plain,
// reusable unit with no shell and no success animation, for use anywhere else
// in the app that needs a loading state. HandoffWaveform/HandoffLoading stay
// reserved for the actual plugin browser-auth handoff (AuthHandoffStatus).

const INK = '42,40,36';
const ACCENT = '230,84,58';
const RATIOS = [0.18, 0.32, 0.46, 0.62, 0.8, 1.0];
const PHASE = 0.18;

const waveAmp = (t: number, time: number) => {
    const slow = Math.sin(t * Math.PI * 2 - time * 1.4);
    const fast = Math.sin(t * Math.PI * 3.7 - time * 2.1) * 0.35;
    return (slow + fast) * 4 * t * (1 - t);
};

function PreloaderWave() {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Layout effect, not a passive effect: it runs before the browser paints
    // the commit that mounted this canvas, so the very first paint already
    // has the wave drawn on it — a passive effect would let one blank-canvas
    // frame through first, which flashes visibly on a remount (unlike on
    // first page load, where there's no prior frame to flash against).
    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let ctx = canvas.getContext('2d')!;
        let w = 0;
        let h = 0;

        const setup = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = (window.devicePixelRatio || 1) * 2;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx = canvas.getContext('2d')!;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            w = rect.width;
            h = rect.height;
        };
        setup();

        // Transparent clear (not a fillRect) so this drops into a cream
        // full-bleed page or a white card alike without a mismatched backing.
        const drawFrame = (time: number) => {
            const cy = h / 2;
            const maxHalf = h * 0.36;
            const res = 200;

            ctx.clearRect(0, 0, w, h);

            RATIOS.forEach((ratio, idx) => {
                const layerPhase = idx * PHASE;
                ctx.strokeStyle = `rgba(${INK},${0.16 + ratio * 0.2})`;
                ctx.lineWidth = 0.55;
                for (const dir of [-1, 1]) {
                    ctx.beginPath();
                    for (let i = 0; i <= res; i++) {
                        const t = i / res;
                        const x = t * w;
                        const amp = waveAmp(t, time + layerPhase) * ratio * maxHalf;
                        const y = cy + dir * amp;
                        if (i === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.stroke();
                }
            });

            // Accent dot sweeping left→right along the crest, every ~2.4s.
            const sT = (time / 2.4) % 1;
            const sY = cy + waveAmp(sT, time + 5 * PHASE) * maxHalf;
            ctx.fillStyle = `rgba(${ACCENT},0.92)`;
            ctx.beginPath();
            ctx.arc(sT * w, sY, 1.4, 0, Math.PI * 2);
            ctx.fill();
        };

        // Time is the raw rAF timestamp (seconds since page load), not "since
        // this mount" — a loading screen commonly unmounts and remounts a new
        // Preloader instance right after this one (e.g. AuthGate's bootstrap
        // check handing off to a page's own loading state). Keying off page
        // time instead of mount time means the wave's phase carries over
        // instead of visibly snapping back to its start pose.
        drawFrame(performance.now() / 1000); // paint immediately, don't wait for the first rAF tick

        let raf = 0;
        const loop = (ms: number) => {
            drawFrame(ms / 1000);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        const onResize = () => setup();
        window.addEventListener('resize', onResize);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return <canvas ref={canvasRef} aria-hidden className="block h-[68px] w-[182px]" />;
}

export function Preloader({ label = 'Loading', className = '' }: { label?: string; className?: string }) {
    return (
        <div className={`flex flex-col items-center gap-[18px] ${className}`}>
            <PreloaderWave />
            <span className="animate-textpulse text-[8px] font-medium uppercase tracking-[0.20em] text-[rgba(42,40,36,0.42)]">
                {label}
            </span>
        </div>
    );
}

// Full-viewport centered treatment, for a loading state that hasn't yet
// decided what page chrome (if any) belongs around it — e.g. the session
// bootstrap check in AuthGate, before we know whether to show a header.
export function FullPagePreloader({ label }: { label?: string }) {
    return (
        <div className="flex min-h-screen w-full items-center justify-center bg-page-cream">
            <Preloader label={label} />
        </div>
    );
}

export default Preloader;
